import { useState } from "react";

import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";
import FilterSingleSelectDropdown from "../../../components/filterDropdown/FilterSingleSelectDropdown";
import { productUnitOfMeasureOptions } from "../../../shared/utility";

export function ProductsViewModel(logout, addSnackbar) {
  const [filters, setFilters] = useState({
    product_category: { value: -1, label: "All categories" },
    product_class: { value: -1, label: "All classes" },
    stock_unit_of_measurement: { value: -1, label: "All units" },
  });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);

  const getProducts = async function (search, page, filter) {
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(
      `products?search=${search}&page=${page}`,
      payload
    );
    if (callResult.success) {
      const response = callResult.data;
      if (page > 1) {
        setProducts([...products, ...response.data]);
      } else {
        setTotalProducts(response.total);
        setProducts(response.data);
        setLoading(false);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const uploadProductsCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadProductsCsv",
      payload,
      "multipart/form-data"
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportProduct = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadProductsCsv}
          setOpen={setEmptyModelOpen}
          title="Products"
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const deleteProductById = async function (productId) {
    let callResult = await makePostApiCall(`deleteProduct/${productId}`);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success
        )
      );

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product");
    }
    return callResult;
  };

  const getProductCategories = async function () {
    let callResult = await makeGetApiCall(`categoriesForDropdown`);
    if (callResult.success) {
      setCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductClasses = async function () {
    let callResult = await makeGetApiCall(`classesForDropdown`);
    if (callResult.success) {
      setClassOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Class"
      onChange={(value) =>
        setFilters({
          ...filters,
          product_class: value,
        })
      }
      options={classOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          product_class: { value: -1, label: "All classes" },
        })
      }
      valueName="product_class"
      value={filters.product_class}
    />,
    <FilterSingleSelectDropdown
      header="Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          product_category: value,
        })
      }
      options={categoryOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          product_category: { value: -1, label: "All categories" },
        })
      }
      valueName="product_category"
      value={filters.product_category}
    />,
    <FilterSingleSelectDropdown
      header="Stock UofM"
      onChange={(value) =>
        setFilters({
          ...filters,
          stock_unit_of_measurement: value,
        })
      }
      options={productUnitOfMeasureOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          stock_unit_of_measurement: { value: -1, label: "All UofM" },
        })
      }
      valueName="stock_unit_of_measurement"
      value={filters.stock_unit_of_measurement}
    />,
  ];

  const resetFilters = () => {
    const filterReset = {
      product_category: { value: -1, label: "All categories" },
    };
    setFilters(filterReset);

    setShowFilters(false);
    getProducts(null, 1, filterReset);
  };

  const askDeleteProduct = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProductById(product.id)}
          message={`Are you sure you want to delete  ${product.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    openImportProduct,
    setEmptyModelOpen,
    emptyModal,
    askDeleteProduct,
    getProductCategories,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getProductClasses,
    totalProducts,
  };
}
